import type { LanguageId } from '../pipeline/types';
import { getParserFor } from '../parsing/tree-sitter';

export interface Chunk {
  path: string;
  text: string;
  startLine: number;
  endLine: number;
}

const SLIDING_WINDOW_LINES = 40;
const SLIDING_WINDOW_OVERLAP = 5;

function slidingWindowChunks(path: string, content: string): Chunk[] {
  const lines = content.split('\n');
  const chunks: Chunk[] = [];
  const step = SLIDING_WINDOW_LINES - SLIDING_WINDOW_OVERLAP;
  for (let start = 0; start < lines.length; start += step) {
    const end = Math.min(start + SLIDING_WINDOW_LINES, lines.length);
    const text = lines.slice(start, end).join('\n');
    if (text.trim()) chunks.push({ path, text, startLine: start, endLine: end - 1 });
    if (end >= lines.length) break;
  }
  return chunks;
}

/** AST-node-based chunking (one chunk per top-level statement) where a grammar is available, falling back to a sliding window otherwise. */
export async function chunkFile(path: string, content: string, languageId: LanguageId | undefined): Promise<Chunk[]> {
  if (languageId) {
    try {
      const parser = await getParserFor(languageId);
      const tree = parser.parse(content);
      if (tree) {
        const chunks = tree.rootNode.namedChildren
          .map((node) => ({
            path,
            text: content.slice(node.startIndex, node.endIndex),
            startLine: node.startPosition.row,
            endLine: node.endPosition.row,
          }))
          .filter((c) => c.text.trim().length > 0);
        if (chunks.length > 0) return chunks;
      }
    } catch {
      // fail open to the sliding-window fallback below
    }
  }
  return slidingWindowChunks(path, content);
}
